import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowRight, Phone, Shield, Clock, ThumbsUp, MapPin } from 'lucide-react';
import { COMPANY_INFO } from '../data/siteData';
import { useServices } from '../context/ServicesContext';

function HeroSection() {
  return (
    <section data-testid="hero-section" className="relative min-h-[600px] lg:min-h-[700px] flex items-center">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_pressure-wash-demo-1/artifacts/p0lku1jo_Eddy%20on%20Metal%20Roof%201.jpg')`,
        }}
      />
      <div className="absolute inset-0 hero-overlay" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-200 mb-4 opacity-0 animate-fade-in-up">
            Professional Pressure Washing
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-white mb-6 opacity-0 animate-fade-in-up animate-delay-100">
            {COMPANY_INFO.name}
          </h1>
          <p className="text-lg sm:text-xl text-sky-100 mb-8 leading-relaxed opacity-0 animate-fade-in-up animate-delay-200 font-body">
            {COMPANY_INFO.tagline}. We bring the shine back to your home, driveways, roofs, and more.
          </p>
        </div>
      </div>
    </section>
  );
}

function WhyUsSection() {
  const reasons = [
    { icon: Shield, title: 'Licensed & Insured', desc: 'Fully covered for your peace of mind' },
    { icon: ThumbsUp, title: 'Satisfaction Guaranteed', desc: 'We ensure you love the results' },
    { icon: Clock, title: 'Quick Turnaround', desc: 'Most jobs completed same day' },
  ];

  return (
    <section data-testid="why-us-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700 mb-3">Why Choose Us</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900">
            Southwest Florida's Trusted Choice
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {reasons.map((r, i) => (
            <div
              key={r.title}
              className="text-center p-8 rounded-2xl bg-sky-50 border border-sky-100"
            >
              <div className="w-14 h-14 bg-sky-700 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <r.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">{r.title}</h3>
              <p className="text-slate-500 text-sm">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  const { services } = useServices();
  return (
    <section data-testid="services-section" id="services" className="py-20 md:py-28 bg-sky-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700 mb-3">What We Do</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900">
            Our Services
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, i) => (
            <Link
              key={service.slug}
              to={`/services/${service.slug}`}
              data-testid={`service-card-${service.slug}`}
              className="service-card bg-white rounded-2xl border border-sky-100 shadow-sm overflow-hidden group"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
                    <service.icon className="w-5 h-5 text-sky-700" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900">{service.name}</h3>
                </div>
                <p className="text-slate-500 text-sm leading-relaxed mb-4">{service.shortDesc}</p>
                <div className="flex items-center text-sky-700 font-medium text-sm group-hover:gap-2 transition-all">
                  Learn More <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </div>
            </Link>
          ))}

          {/* Something Unique card */}
          <Link
            to="/contact"
            data-testid="service-card-something-unique"
            className="service-card bg-sky-700 rounded-2xl border border-sky-600 shadow-sm overflow-hidden group"
          >
            <div className="h-48 bg-sky-800 flex items-center justify-center">
              <Phone className="w-16 h-16 text-sky-400 group-hover:scale-110 transition-transform duration-500" />
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-white">Something Unique?</h3>
              </div>
              <p className="text-sky-200 text-sm leading-relaxed mb-4">Have a custom project in mind? Let us know and we'll give you a call back with a personalized quote.</p>
              <div className="flex items-center text-white font-medium text-sm group-hover:gap-2 transition-all">
                Get a Call Back <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section data-testid="cta-section" className="py-20 md:py-28 bg-sky-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-white mb-4">
          Ready to Transform Your Property?
        </h2>
        <p className="text-sky-200 text-base sm:text-lg mb-8 max-w-xl mx-auto">
          Get a free, no-obligation estimate today. We'll have your property looking brand new in no time.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/contact"
            data-testid="cta-get-quote-btn"
            className="bg-white text-sky-700 font-semibold rounded-full px-8 py-3.5 hover:bg-sky-50 transition-colors shadow-lg"
          >
            Get Your Free Estimate
          </Link>
          <span
            className="border-2 border-white/40 text-white font-semibold rounded-full px-8 py-3.5 flex items-center justify-center gap-2"
          >
            <Phone className="w-4 h-4" />
            {COMPANY_INFO.phone}
          </span>
        </div>
      </div>
    </section>
  );
}

function ServiceAreaSection() {
  const areas = ['Naples', 'Marco Island', 'Ave Maria', 'Bonita Springs', 'Estero', 'Immokalee'];

  return (
    <section data-testid="service-area-section" className="py-14 bg-sky-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <MapPin className="w-5 h-5 text-sky-200" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-200">Proudly Serving</p>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          {areas.map((area) => (
            <span key={area} className="bg-white/10 text-white text-sm font-medium px-5 py-2 rounded-full border border-white/20">
              {area}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useEffect(() => {
    if (window.location.hash === '#services') {
      setTimeout(() => {
        document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, []);

  return (
    <div data-testid="home-page">
      <HeroSection />
      <WhyUsSection />
      <ServiceAreaSection />
      <ServicesSection />
      <CTASection />
    </div>
  );
}
